import { useEffect, useCallback, useRef } from "react"

/**
 * Custom hook for handling resize functionality with drag interactions
 * @param {Object} options - Configuration options
 * @param {boolean} options.isDragging - Whether dragging is currently active
 * @param {Function} options.setIsDragging - Function to set dragging state
 * @param {Function} options.setContentWidth - Function to update content width
 * @param {Function} options.onDragEnd - Callback when drag ends, receives final width
 * @param {number} options.minWidthPercent - Minimum width as percentage of viewport (default: 25)
 * @param {number} options.maxWidthPercent - Maximum width as percentage of viewport (default: 50)
 */
export default function useResizeHandler({
  isDragging,
  setIsDragging,
  setContentWidth,
  onDragEnd,
  minWidthPercent = 25,
  maxWidthPercent = 50,
}) {
  // Track the latest width during drag for accurate reporting
  const currentWidthRef = useRef(0)

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return

    const viewportWidth = window.innerWidth
    const newWidth = viewportWidth - e.clientX
    const minWidth = viewportWidth * (minWidthPercent / 100)
    const maxWidth = viewportWidth * (maxWidthPercent / 100)

    if (newWidth >= minWidth && newWidth <= maxWidth) {
      currentWidthRef.current = newWidth
      setContentWidth(newWidth)
    }
  }, [isDragging, setContentWidth, minWidthPercent, maxWidthPercent])

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return

    setIsDragging(false)

    const finalWidth = currentWidthRef.current

    // Call the drag end callback with the final width
    if (onDragEnd) {
      onDragEnd(finalWidth)
    }
  }, [isDragging, setIsDragging, onDragEnd])

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true)
    e.preventDefault()
    e.stopPropagation()
  }, [setIsDragging])

  // Set up and clean up event listeners
  useEffect(() => {
    if (!isDragging) return

    document.addEventListener("mousemove", handleMouseMove, { passive: false })
    document.addEventListener("mouseup", handleMouseUp)

    // Prevent text selection during drag
    document.body.style.userSelect = "none"
    document.body.style.cursor = "ew-resize"

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)

      // Restore normal cursor and text selection
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return {
    handleMouseDown,
  }
}
